import React, { useState } from 'react'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import ContactSupportModal from '../components/ContactSupportModal'

const FAQPage = () => {
  const [openItem, setOpenItem] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)

  const faqCategories = [
    {
      title: 'Getting Started',
      faqs: [
        {
          question: 'How do I start my first conversation with AI?',
          answer: 'Simply choose your AI teacher, select a conversation topic, and click "Start Practice". Begin with a simple greeting like "Hello, how are you?" and let the conversation flow naturally. Our AI is designed to be patient and helpful for beginners.'
        },
        {
          question: 'What English level do I need to use VivaLingua?',
          answer: 'VivaLingua works for all levels! Whether you\'re an absolute beginner or advanced learner, our AI adapts to your skill level. Start with basic greetings and simple sentences, then gradually work up to more complex conversations.'
        },
        {
          question: 'Do I need special equipment?',
          answer: 'You only need a device with a microphone and speakers (or headphones). Most smartphones, tablets, and computers work perfectly. We recommend using headphones for the best audio quality and to avoid echo.'
        }
      ]
    },
    {
      title: 'AI Teachers & Conversations',
      faqs: [
        {
          question: 'How do AI teachers work?',
          answer: 'Our AI teachers use advanced natural language processing to understand your speech and respond naturally. They can adapt their teaching style, speed, and complexity based on your needs. Each AI teacher has different personalities and specializations.'
        },
        {
          question: 'Can the AI correct my pronunciation?',
          answer: 'Yes! Our AI provides real-time pronunciation feedback. It will gently correct mispronunciations and help you practice difficult sounds. You can ask for specific pronunciation help anytime during your conversation.'
        },
        {
          question: 'What if I don\'t understand what the AI says?',
          answer: 'Just ask! Say "Can you repeat that?" or "I don\'t understand, can you explain?" The AI can speak slower, use simpler words, or explain concepts in different ways until you understand.'
        }
      ]
    },
    {
      title: 'Technical Support',
      faqs: [
        {
          question: 'The AI can\'t hear me properly. What should I do?',
          answer: 'First, check that your microphone is working and browser permissions are enabled. Try speaking clearly and closer to the microphone. If issues persist, try using headphones or check your internet connection.'
        },
        {
          question: 'Why is there a delay in the AI responses?',
          answer: 'Small delays (1-2 seconds) are normal as the AI processes your speech. Longer delays might indicate internet connectivity issues. Try refreshing the page or checking your internet connection.'
        },
        {
          question: 'Can I use VivaLingua on my mobile phone?',
          answer: 'Absolutely! VivaLingua works on all devices including smartphones and tablets. The mobile experience is optimized for touch and voice interaction.'
        }
      ]
    },
    {
      title: 'Learning & Progress',
      faqs: [
        {
          question: 'How often should I practice with AI?',
          answer: 'We recommend daily practice, even if just for 10-15 minutes. Consistency is more important than duration. Regular short sessions are more effective than occasional long ones.'
        },
        {
          question: 'How do I track my progress?',
          answer: 'VivaLingua automatically tracks your conversation time, vocabulary learned, and pronunciation improvements. You can view your progress in the dashboard and see detailed analytics about your learning journey.'
        },
        {
          question: 'Can I practice specific topics or skills?',
          answer: 'Yes! You can choose from various conversation topics like business English, travel, daily life, or academic discussions. You can also focus on specific skills like pronunciation, grammar, or vocabulary building.'
        }
      ]
    }
  ]

  const allFaqs = faqCategories.flatMap((category, categoryIndex) =>
    category.faqs.map((faq, faqIndex) => ({
      ...faq,
      category: category.title,
      id: `${categoryIndex}-${faqIndex}`
    }))
  )

  const filteredFaqs = allFaqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.category.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const toggleItem = (id: string) => {
    setOpenItem(openItem === id ? null : id)
  }

  return (
    <div className="px-4 py-12 sm:px-6 lg:px-8 animate-fade-in">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Find answers to common questions about using VivaLingua for English speaking practice with AI.
          </p>
        </div>

        {/* Search */}
        <div className="mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search FAQ..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
            />
          </div>
        </div>

        {/* FAQ Content */}
        {searchQuery ? (
          // Search Results
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-white mb-4">
              Search Results ({filteredFaqs.length})
            </h2>
            {filteredFaqs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-slate-400">No results found for "{searchQuery}"</p>
              </div>
            ) : (
              filteredFaqs.map((faq) => (
                <div
                  key={faq.id}
                  className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700"
                >
                  <button
                    onClick={() => toggleItem(faq.id)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-700/30 transition-colors rounded-xl"
                  >
                    <div>
                      <h3 className="text-lg font-medium text-white mb-1">{faq.question}</h3>
                      <p className="text-sm text-slate-400">{faq.category}</p>
                    </div>
                    {openItem === faq.id ? (
                      <ChevronUp className="h-5 w-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-slate-400" />
                    )}
                  </button>
                  {openItem === faq.id && (
                    <div className="px-6 pb-4">
                      <p className="text-slate-300 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        ) : (
          // Category View
          <div className="space-y-8">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="bg-slate-800/30 rounded-xl p-6 border border-slate-700">
                <h2 className="text-2xl font-semibold text-white mb-6">{category.title}</h2>
                <div className="space-y-4">
                  {category.faqs.map((faq, faqIndex) => {
                    const itemId = `${categoryIndex}-${faqIndex}`
                    return (
                      <div
                        key={faqIndex}
                        className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700"
                      >
                        <button
                          onClick={() => toggleItem(itemId)}
                          className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-slate-700/30 transition-colors rounded-lg"
                        >
                          <h3 className="text-lg font-medium text-white">{faq.question}</h3>
                          {openItem === itemId ? (
                            <ChevronUp className="h-5 w-5 text-slate-400" />
                          ) : (
                            <ChevronDown className="h-5 w-5 text-slate-400" />
                          )}
                        </button>
                        {openItem === itemId && (
                          <div className="px-6 pb-4">
                            <p className="text-slate-300 leading-relaxed">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Contact Support */}
        <div className="mt-12 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl p-8 border border-blue-500/30 text-center">
          <h2 className="text-2xl font-semibold text-white mb-4">Still Have Questions?</h2>
          <p className="text-slate-300 mb-6">
            Can't find what you're looking for? Our support team is here to help you succeed with your English learning journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
            >
              Contact Support
            </button>
            <a
              href="#"
              className="inline-flex items-center px-6 py-3 border border-slate-600 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all duration-200"
            >
              Join Community
            </a>
          </div>
        </div>
      </div>

      <ContactSupportModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </div>
  )
}

export default FAQPage